// src/screens/admin/stockHistory/StockHistory.jsx
//
// Data: local useState dummy data
// To replace with API: swap DUMMY_HISTORY with your API call in useEffect

import { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  History,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Search,
  ChevronDown,
  Check,
  ArrowLeft,
} from "lucide-react";
import "../../../assets/css/admin/stockHistory/StockHistory.css";
import axios from "axios";
import AppContext from "../../../context/AppContext";

// ─────────────────────────────────────────────────────────────
// TYPE CONFIG
// ─────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  restock: { label: "Restock", className: "sh-type-restock", Icon: RefreshCw },
  adjustment: {
    label: "Adjustment",
    className: "sh-type-adjustment",
    Icon: TrendingUp,
  },
  consumption: {
    label: "Consumption",
    className: "sh-type-consumption",
    Icon: TrendingDown,
  },
};

// ─────────────────────────────────────────────────────────────
// MATERIAL FILTER DROPDOWN  (own state + outside-click)
// ─────────────────────────────────────────────────────────────
const MaterialDropdown = ({ value, onChange, materials }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedLabel =
    value === "all"
      ? "All Materials"
      : (materials.find((m) => m.id === value)?.name ?? "All Materials");

  return (
    <div className="sh-dropdown-wrapper" ref={wrapperRef}>
      <button
        className="sh-dropdown-btn"
        onClick={() => setIsOpen((p) => !p)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedLabel}</span>
        <ChevronDown
          size={16}
          className={`sh-dropdown-chevron ${isOpen ? "open" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="sh-dropdown-menu" role="listbox">
          <div
            className={`sh-dropdown-option ${value === "all" ? "active" : ""}`}
            onClick={() => {
              onChange("all");
              setIsOpen(false);
            }}
          >
            <span>All Materials</span>
            {value === "all" && (
              <Check size={14} className="sh-dropdown-check" />
            )}
          </div>
          {materials.map((m) => (
            <div
              key={m.id}
              className={`sh-dropdown-option ${value === m.id ? "active" : ""}`}
              role="option"
              aria-selected={value === m.id}
              onClick={() => {
                onChange(m.id);
                setIsOpen(false);
              }}
            >
              <span>{m.name}</span>
              {value === m.id && (
                <Check size={14} className="sh-dropdown-check" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────
const StockHistory = () => {
  const navigate = useNavigate();
  // ── To replace with API: useEffect mein fetch karo, setHistory(data) ──
  const [history, setHistory] = useState([]);
  const [materials, setMaterials] = useState([]);
  const {baseUrl, authToken} = useContext(AppContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [materialFilter, setMaterialFilter] = useState("all");

  const viewAllInventory = () => {
    console.log("view inventory called");
    axios.get(`${baseUrl}/admin/inventory/viewAllInventory`, {
      headers: {
        Authorization: `Bearer${authToken}`
      }
    })
    .then((response) => {
      setMaterials(response.data.rawMaterials);
    })
    .catch((error) => {
      console.log(error)
    })
  }

  const viewAllInventoryHistory = () => {
    console.log("View History Called!");
    axios.get(`${baseUrl}/admin/inventory/viewAllInventoryHistory`, {
      headers: {
        Authorization: `Bearer${authToken}`
      }
    })
    .then((response) => {
      console.log(response.data);
      setHistory(response.data.rawMaterials);
    }).catch((error) => {
      console.log(error);
    })
  }

  useEffect(() => {
    viewAllInventoryHistory();
    viewAllInventory();
  }, []);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-PK", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString("en-PK", {
      hour: "2-digit",
      minute: "2-digit",
    });

  // ── Stats ──
  const totalRestocks = history.filter((e) => e.type === "restock").length;
  const totalAdjustments = history.filter(
    (e) => e.type === "adjustment",
  ).length;
  const totalConsumption = history.filter(
    (e) => e.type === "consumption",
  ).length;
  const totalAdded = history
    .filter((e) => e.type === "restock")
    .reduce((sum, e) => sum + e.quantityAdded, 0);

  // ── Filter + search ──
  const filtered = history.filter((entry) => {
    const matchesType = activeFilter === "all" || entry.type === activeFilter;
    const matchesMaterial =
      materialFilter === "all" || entry.admaterial_id === materialFilter;
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !q ||
      entry.admaterial?.name.toLowerCase().includes(q) ||
      entry.performedby.toLowerCase().includes(q); 
    return matchesType && matchesMaterial && matchesSearch;
  });

  return (
    <div className="sh-container">
      {/* ── Back Button ── */}
      <button className="sh-back-btn" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back
      </button>

      {/* ── Header ── */}
      <div className="sh-header">
        <h1 className="sh-title">Stock History</h1>
        <p className="sh-subtitle">Complete log of all inventory movements</p>
      </div>

      {/* ── Stats ── */}
      <div className="sh-stats-grid">
        <div className="sh-stat-card">
          <div className="sh-stat-icon sh-icon-total">
            <History size={22} />
          </div>
          <div className="sh-stat-content">
            <div className="sh-stat-value">{history.length}</div>
            <div className="sh-stat-label">Total Events</div>
          </div>
        </div>
        <div className="sh-stat-card">
          <div className="sh-stat-icon sh-icon-restock">
            <RefreshCw size={22} />
          </div>
          <div className="sh-stat-content">
            <div className="sh-stat-value">{totalRestocks}</div>
            <div className="sh-stat-label">Restocks</div>
          </div>
        </div>
        <div className="sh-stat-card">
          <div className="sh-stat-icon sh-icon-consumption">
            <TrendingDown size={22} />
          </div>
          <div className="sh-stat-content">
            <div className="sh-stat-value">{totalConsumption}</div>
            <div className="sh-stat-label">Consumption</div>
          </div>
        </div>
        <div className="sh-stat-card">
          <div className="sh-stat-icon sh-icon-added">
            <TrendingUp size={22} />
          </div>
          <div className="sh-stat-content">
            <div className="sh-stat-value">{totalAdded}</div>
            <div className="sh-stat-label">Total Units Added</div>
          </div>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="sh-controls">
        <div className="sh-filters">
          {[
            { key: "all", label: "All Events", count: history.length },
            { key: "restock", label: "Restocks", count: totalRestocks },
            {
              key: "adjustment",
              label: "Adjustments",
              count: totalAdjustments,
            },
            {
              key: "consumption",
              label: "Consumption",
              count: totalConsumption,
            },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              className={`sh-filter-btn ${activeFilter === key ? "active" : ""}`}
              onClick={() => setActiveFilter(key)}
            >
              {label}
              <span className="sh-filter-count">{count}</span>
            </button>
          ))}
        </div>

        <div className="sh-right-controls">
          <MaterialDropdown
            value={materialFilter}
            onChange={setMaterialFilter}
            materials={materials}
          />
          <div className="sh-search">
            <Search size={15} className="sh-search-icon" />
            <input
              type="text"
              className="sh-search-input"
              placeholder="Search material, performed by..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="sh-table-container">
        {filtered.length === 0 ? (
          <div className="sh-empty">
            <span className="sh-empty-icon">📋</span>
            <h3>
              {history.length === 0 ? "No history yet" : "No results found"}
            </h3>
            <p>
              {history.length === 0
                ? "Restock events will appear here"
                : "Try adjusting your search or filters"}
            </p>
          </div>
        ) : (
          <table className="sh-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Material</th>
                <th>Type</th>
                <th>Before</th>
                <th>Change</th>
                <th>After</th>
                <th>Performed By</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => {
                const config = TYPE_CONFIG[entry.type] ?? TYPE_CONFIG.restock;
                const isNegative = entry.type === "consumption";
                return (
                  <tr key={entry.id}>
                    <td>
                      <div className="sh-date">
                        {formatDate(entry.created_at)}
                      </div>
                      <div className="sh-time">
                        {formatTime(entry.created_at)}
                      </div>
                    </td>
                    <td>
                      <span className="sh-material-name">
                        {entry.admaterial?.name}
                      </span>
                    </td>
                    <td>
                      <span className={`sh-type-badge ${config.className}`}>
                        <config.Icon size={12} />
                        {config.label}
                      </span>
                    </td>
                    <td className="sh-qty-before">
                      {entry.quantityBefore} {entry.unit}
                    </td>
                    <td>
                      <span
                        className={`sh-qty-change ${isNegative ? "sh-qty-negative" : "sh-qty-positive"}`}
                      >
                        {isNegative ? "−" : "+"}
                        {Math.abs(entry.quantityAdded)} {entry.unit}
                      </span>
                    </td>
                    <td className="sh-qty-after">
                      {entry.quantityAfter} {entry.unit}
                    </td>
                    <td className="sh-performed-by">{entry.performedby}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StockHistory;